import React, { useState, useEffect, useCallback } from 'react';
import SEO from '../components/SEO';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { Target, Users, Award, Lightbulb } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  'Our Mission': <Target size={32} className="text-accent" />,
  'Innovation': <Lightbulb size={32} className="text-accent" />,
  'Customer Focus': <Users size={32} className="text-accent" />,
  'Excellence': <Award size={32} className="text-accent" />,
};

const DEFAULTS = {
  heading: "Trusted Partner for Custom Net-Zero Solutions",
  description: "India's fastest growing pureplay Commercial & Industrial (C&I) renewable energy company, Invictus Green Energy Solutions LLP aims to bridge ambition with reality for conscious enterprises of tomorrow, today.",
  who_we_are_title: "Who We Are",
  who_we_are_text: "<p>Invictus Green Energy Solutions LLP follows a strategic approach to environmental care, focusing on climate change action, energy efficiency, responsible water use, waste reduction, and protecting biodiversity. Our main goal is to minimize ecological impact across all our operations and supply chain, while accelerating the shift of commercial and Industrial sector to clean energy through prudent financing and sustainable engineering, helping them adapt net zero without any hassles.</p><p>The vision of Invictus Green Energy Solutions LLP is to provide Zero Investment, maximum value, Hassle free operations, predictable fixed Tariffs for longer period of operations, financial and operational flexibility to its clients.</p><p>The Invictus Green was started by partners cum founders <strong>Rupinder Kaur (MBA)</strong> and <strong>Mundeep Singh Batra (B.Tech)</strong> with the same vision from their erstwhile office in the year 2024 and since then it has started bearing results, as the Company today has operational power plants of 3.5 MW and has 10 MW under Installations and various stages of commissioning.</p><p>The team has dedicated O&amp;M teams and experts having more than 15 years of expertise in designing, installation, commissioning and in the field of Solar power plants.</p><p>The team has achieved more than 55 MW of CAPEX as well as OPEX model of Solar plant installations / commissioning and is steering towards achieving the target of 100 MW by the end of Financial year 2027-28.</p><p>The company aims to play pivotal role in accelerating the adaptation of sustainable Energy vis a vis Renewable energy mediums like solar, wind, other hydrocarbon based energy plants and Battery Energy Storage Systems in the near future term.</p>",
  image_url: "https://images.unsplash.com/photo-1726221062287-fda475b85493?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxlbmdpbmVlcnMlMjB3b3JraW5nJTIwb24lMjBzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3NzYyNjEyODF8MA&ixlib=rb-4.1.0&q=85",
  value_1_title: "Our Mission",
  value_1_description: "To minimize ecological impact across all operations and supply chain, while accelerating the shift of C&I sector to clean energy through prudent financing and sustainable engineering.",
  value_2_title: "Innovation",
  value_2_description: "We leverage cutting-edge technology and innovative approaches to design and implement solar solutions that maximize efficiency and ROI for our clients.",
  value_3_title: "Customer Focus",
  value_3_description: "Zero Investment, maximum value, hassle-free operations, predictable fixed tariffs for longer period of operations, and complete financial and operational flexibility.",
  value_4_title: "Excellence",
  value_4_description: "With 55+ MW of CAPEX and OPEX installations achieved, our dedicated teams bring 15+ years of expertise in designing, installation, commissioning and O&M of solar power plants.",
  stat_1_value: "55+",
  stat_1_label: "MW Installed Capacity",
  stat_2_value: "10+",
  stat_2_label: "MW Under Installation",
  stat_3_value: "100 MW",
  stat_3_label: "Target by FY 2027-28",
};

const About = () => {
  const [pageData, setPageData] = useState(DEFAULTS);

  const fetchContent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/pages/about`);
      if (data.content && Object.keys(data.content).length > 0) {
        setPageData(prev => ({ ...prev, ...data.content }));
      }
    } catch (error) {
      console.error('About page content fetch failed:', error);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const getImageSrc = (url) => {
    if (!url) return DEFAULTS.image_url;
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  const values = [
    { title: pageData.value_1_title, description: pageData.value_1_description },
    { title: pageData.value_2_title, description: pageData.value_2_description },
    { title: pageData.value_3_title, description: pageData.value_3_description },
    { title: pageData.value_4_title, description: pageData.value_4_description },
  ];

  return (
    <div>
      <SEO
        title="About Us - RESCO Invictus Green Energy Solutions"
        description={pageData.description}
        keywords="about us, solar energy company, C&I solar provider, green energy, net zero"
        url="/about"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:py-32 bg-surface" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-4 block" data-testid="about-overline">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-primary mb-6" data-testid="about-title">
              {pageData.heading}
            </h1>
            <p className="text-base sm:text-lg leading-relaxed text-text_muted" data-testid="about-description">
              {pageData.description}
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-16 md:py-24" data-testid="about-image-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-primary mb-10 text-center" data-testid="who-we-are-title">
            {pageData.who_we_are_title}
          </h2>

          {/* Banner Image */}
          <img
            src={getImageSrc(pageData.image_url)}
            alt="Invictus Green Energy Solutions team"
            className="w-full h-64 md:h-80 object-cover rounded-sm shadow-lg mb-10"
            data-testid="about-image"
          />

          {/* Full-width content */}
          <div
            className="text-base md:text-lg leading-loose text-text_muted overflow-hidden [&_p]:mb-5 [&_strong]:text-primary [&_strong]:font-semibold [&_u]:underline [&_u]:decoration-accent [&_u]:underline-offset-4"
            style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
            data-testid="who-we-are-text"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pageData.who_we_are_text) }}
          />
        </div>
      </section>

      {/* Meet Our Founders */}
      <section className="py-16 md:py-24 bg-surface" data-testid="founders-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-3 block">Leadership</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-primary" data-testid="founders-title">
              Meet Our Founders
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Mundeep Singh Batra */}
            <div className="bg-white rounded-sm border border-border overflow-hidden text-center hover:shadow-lg transition-shadow" data-testid="founder-mundeep">
              <div className="pt-10 px-8">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/invictus-green/uploads/909d2217-d2b5-4ab2-a81d-f14e91c7cc22.jpeg`}
                  alt="Mundeep Singh Batra"
                  className="w-48 h-48 object-cover object-top rounded-full mx-auto border-4 border-accent/20 shadow-md"
                />
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-primary mb-1">Mundeep Singh Batra</h3>
                <p className="text-accent font-semibold text-sm mb-1">CEO & Co-Founder</p>
                <p className="text-text_muted text-sm">B.Tech</p>
              </div>
            </div>

            {/* Rupinder Kaur */}
            <div className="bg-white rounded-sm border border-border overflow-hidden text-center hover:shadow-lg transition-shadow" data-testid="founder-rupinder">
              <div className="pt-10 px-8">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/invictus-green/uploads/b91b73d0-6049-4828-8fae-623c4b8e91d2.png`}
                  alt="Rupinder Kaur"
                  className="w-48 h-48 object-cover object-top rounded-full mx-auto border-4 border-accent/20 shadow-md"
                />
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-primary mb-1">Rupinder Kaur</h3>
                <p className="text-accent font-semibold text-sm mb-1">CFO & Co-Founder</p>
                <p className="text-text_muted text-sm">MBA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-surface" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-primary mb-4" data-testid="values-title">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value) => (
              <div
                key={value.title}
                className="bg-white p-8 rounded-sm border border-border"
                data-testid={`value-card-${value.title}`}
              >
                <div className="mb-4">{ICON_MAP[value.title] || <Target size={32} className="text-accent" />}</div>
                <h3 className="text-xl font-semibold text-primary mb-3">{value.title}</h3>
                <p className="text-text_muted leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-primary mb-4" data-testid="why-choose-title">
              Why Choose Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center" data-testid="why-1">
              <div className="text-5xl font-bold text-accent mb-2">{pageData.stat_1_value}</div>
              <div className="text-text_muted font-medium">{pageData.stat_1_label}</div>
            </div>
            <div className="text-center" data-testid="why-2">
              <div className="text-5xl font-bold text-accent mb-2">{pageData.stat_2_value}</div>
              <div className="text-text_muted font-medium">{pageData.stat_2_label}</div>
            </div>
            <div className="text-center" data-testid="why-3">
              <div className="text-5xl font-bold text-accent mb-2">{pageData.stat_3_value}</div>
              <div className="text-text_muted font-medium">{pageData.stat_3_label}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
